import { Button } from "@mui/material";
import EditRawJSONDialog from "controls/EditRawJSONDialog";
import FormFieldContainer from "controls/FormFieldContainer";
import ImageCanvas from "controls/ImageCanvas";
import useLoadedImage from "helpers/useLoadedImage";
import useFormDialogs from "hooks/useFormDialogs";
import { isEqualWith } from "lodash-es";
import React, { useState } from "react";
import { Rnd } from "react-rnd";
import { useComponentSize } from "react-use-size";

export default function RectInput({
  value,
  onChange,
  disabled = false,
  width = 1,
  height = 1,
  maxWidth = null,
  maxHeight = null,
  lockFillImageAspectRatio = false,
  allowedRects = null,
  aspectRatio = width && height ? width / height : 1,
  round = false,
  fillImage = null,
  fillImageUrl = null,
  fillImageVideoTime = 0,
  backgroundImage = null,
  backgroundImageUrl = null,
  backgroundImageVideoTime = 0,
  enableRawJSON = false,
  initValue,
  getInitValue,
  ...others
}) {
  const [loadedBackgroundImage] = useLoadedImage(backgroundImageUrl, { videoTime: backgroundImageVideoTime });
  const [loadedFillImage] = useLoadedImage(fillImageUrl, { videoTime: fillImageVideoTime });

  fillImage ||= loadedFillImage;
  backgroundImage ||= loadedBackgroundImage;

  // natural aspect ratio of the fill image, so the area cannot stretch it
  const fillImageAspectRatio =
    (lockFillImageAspectRatio &&
      fillImage &&
      (fillImage.width || fillImage.videoWidth) / (fillImage.height || fillImage.videoHeight)) ||
    null;

  const valueValid =
    !!value && value.constructor === Object && isEqualWith(Object.keys(value).sort(), ["height", "width", "x", "y"]);
  const [editing, editingSet] = useState(false);
  const canvasContainerSize = useComponentSize();
  const [creatingClickedDownAt, creatingClickedDownAtSet] = useState(null);
  const [creatingMoveAt, creatingMoveAtSet] = useState(null);

  // max size in canvas pixels, the props are in value units
  const canvasMaxWidth = maxWidth ? (maxWidth / width) * canvasContainerSize.width : Infinity;
  const canvasMaxHeight = maxHeight ? (maxHeight / height) * canvasContainerSize.height : Infinity;

  // fit a size into the maximum, keeping the fill image aspect ratio when it is locked
  const fitSize = (rectWidth, rectHeight, fitMaxWidth, fitMaxHeight) => {
    rectWidth = Math.min(rectWidth, fitMaxWidth);
    rectHeight = Math.min(rectHeight, fitMaxHeight);
    if (fillImageAspectRatio) {
      rectHeight = Math.min(rectHeight, rectWidth / fillImageAspectRatio);
      rectWidth = rectHeight * fillImageAspectRatio;
    }
    return [rectWidth, rectHeight];
  };

  const creatingRect =
    creatingClickedDownAt &&
    creatingMoveAt &&
    (() => {
      const [rectWidth, rectHeight] = fitSize(
        Math.abs(creatingClickedDownAt[0] - creatingMoveAt[0]),
        Math.abs(creatingClickedDownAt[1] - creatingMoveAt[1]),
        canvasMaxWidth,
        canvasMaxHeight,
      );
      return {
        // keep the corner the drag started from anchored once the size is capped
        left:
          creatingMoveAt[0] < creatingClickedDownAt[0]
            ? creatingClickedDownAt[0] - rectWidth
            : creatingClickedDownAt[0],
        top:
          creatingMoveAt[1] < creatingClickedDownAt[1]
            ? creatingClickedDownAt[1] - rectHeight
            : creatingClickedDownAt[1],
        width: rectWidth,
        height: rectHeight,
      };
    })();

  const insideArea = (rect, area) =>
    rect.x >= area.x &&
    rect.y >= area.y &&
    rect.x + rect.width <= area.x + area.width &&
    rect.y + rect.height <= area.y + area.height;

  const clampIntoArea = (rect, area) => ({
    ...rect,
    x: Math.min(Math.max(rect.x, area.x), area.x + area.width - rect.width),
    y: Math.min(Math.max(rect.y, area.y), area.y + area.height - rect.height),
  });

  // the allowed area a rect sits in, or the one it would take the shortest move to reach
  const areaFor = (rect) => {
    if (!allowedRects?.length) return null;

    return (
      allowedRects.find((area) => insideArea(rect, area)) ||
      allowedRects
        .filter((area) => rect.width <= area.width && rect.height <= area.height)
        .map((area) => {
          const to = clampIntoArea(rect, area);
          return { area, distance: (to.x - rect.x) ** 2 + (to.y - rect.y) ** 2 };
        })
        .reduce((best, move) => (!best || move.distance < best.distance ? move : best), null)?.area ||
      null
    );
  };

  const moveIntoAllowedRects = (rect) => {
    const area = areaFor(rect);
    return area ? clampIntoArea(rect, area) : rect;
  };

  const onChangeWithRound = (value) => {
    value = { ...value };
    [value.width, value.height] = fitSize(value.width, value.height, maxWidth || Infinity, maxHeight || Infinity);
    if (round) {
      value.width = Math.round(value.width);
      value.height = Math.round(value.height);
    }
    value = moveIntoAllowedRects(value);
    if (round) {
      value.x = Math.round(value.x);
      value.y = Math.round(value.y);
    }
    onChange?.(value);
  };

  // the rect the user is currently drawing, dragging or has already set, in value units
  const [formDialogs, formDialogsOpen] = useFormDialogs();

  return (
    <FormFieldContainer data-rect-input disabled={disabled} {...others}>
      {formDialogs}
      <div style={{ display: "flex", flexFlow: "row wrap", alignItems: "center", gap: 5, padding: 5 }}>
        {!!value && !valueValid && <>Invalid value</>}
        {enableRawJSON && (
          <Button
            variant="outlined"
            color="inherit"
            disabled={disabled}
            onClick={async () => {
              const valueNew = await formDialogsOpen(<EditRawJSONDialog value={value} />);
              onChange(valueNew);
            }}
          >
            Raw JSON
          </Button>
        )}
        <Button variant="outlined" disabled={disabled} onClick={() => editingSet((e) => !e)}>
          {editing ? "Finish Edit" : "Edit"}
        </Button>
        <Button variant="outlined" disabled={disabled} onClick={() => onChange(null)}>
          Clear
        </Button>
        {!value && (initValue || getInitValue) && (
          <Button
            variant="outlined"
            disabled={disabled}
            onClick={() => {
              const valueNew = initValue || getInitValue({ width, height });
              onChange(valueNew);
            }}
          >
            Init
          </Button>
        )}
        {editing && valueValid && <>You can move or resize the rectangle area.</>}
        {editing && !valueValid && <>Area not set. Click and drag to create a rectangle area.</>}
        <div
          data-rect-input-canvas
          ref={canvasContainerSize.ref}
          style={{
            overflow: "hidden",
            userSelect: "none",
            position: "relative",
            width: editing ? "100%" : 120,
            height: canvasContainerSize.width / aspectRatio || 1,
            outline: "1px solid black",
            ...(!valueValid &&
              editing && {
                cursor: "crosshair",
              }),
            backgroundColor: "#f0f0f0",
          }}
          {...(!valueValid &&
            editing && {
              onMouseDown: (event) => {
                const boundingClientRect = event.currentTarget.getBoundingClientRect();
                const [x, y] = [event.clientX - boundingClientRect.left, event.clientY - boundingClientRect.top];
                creatingClickedDownAtSet([x, y]);
                creatingMoveAtSet([x, y]);
              },
              onMouseMove: (event) => {
                const boundingClientRect = event.currentTarget.getBoundingClientRect();
                const [x, y] = [event.clientX - boundingClientRect.left, event.clientY - boundingClientRect.top];
                creatingMoveAtSet([x, y]);
              },
              onMouseUp: () => {
                creatingClickedDownAtSet(null);
                creatingMoveAtSet(null);
                onChangeWithRound({
                  x: (creatingRect.left / canvasContainerSize.width) * width,
                  y: (creatingRect.top / canvasContainerSize.height) * height,
                  width: (creatingRect.width / canvasContainerSize.width) * width,
                  height: (creatingRect.height / canvasContainerSize.height) * height,
                });
              },
            })}
        >
          {backgroundImage && (
            <ImageCanvas
              image={backgroundImage}
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                userSelect: "none",
              }}
            />
          )}
          {!valueValid && creatingRect && (
            <ImageCanvas
              image={fillImage}
              style={{
                position: "absolute",
                ...creatingRect,
                outline: "1px dashed red",
                pointerEvents: "none",
              }}
            />
          )}
          {valueValid && (
            <Rnd
              enableResizing={editing}
              disableDragging={!editing}
              bounds="parent"
              maxWidth={maxWidth ? canvasMaxWidth : undefined}
              maxHeight={maxHeight ? canvasMaxHeight : undefined}
              lockAspectRatio={fillImageAspectRatio || false}
              style={{
                outline: "1px dashed red",
              }}
              position={{
                x: (value.x / width) * canvasContainerSize.width,
                y: (value.y / height) * canvasContainerSize.height,
              }}
              size={{
                width: (value.width / width) * canvasContainerSize.width,
                height: (value.height / height) * canvasContainerSize.height,
              }}
              onDragStop={(event, position) => {
                onChangeWithRound({
                  x: (position.x / canvasContainerSize.width) * width,
                  y: (position.y / canvasContainerSize.height) * height,
                  width: value.width,
                  height: value.height,
                });
              }}
              onResize={(event, direction, ref, delta, position) => {
                onChangeWithRound({
                  x: (position.x / canvasContainerSize.width) * width,
                  y: (position.y / canvasContainerSize.height) * height,
                  width: (ref.offsetWidth / canvasContainerSize.width) * width,
                  height: (ref.offsetHeight / canvasContainerSize.height) * height,
                });
              }}
            >
              <ImageCanvas
                image={fillImage}
                style={{
                  pointerEvents: "none",
                  userSelect: "none",
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  ...(!fillImage && {
                    backgroundColor: "rgba(255, 255, 255, 0.5)",
                  }),
                }}
              />
            </Rnd>
          )}
        </div>
      </div>
    </FormFieldContainer>
  );
}
